import { PermissionCode } from "../../permissions/permissions.js";
import {
  USC_TECHNIQUE_OPTIONS,
  availablePointsForRow,
  isJointTypeSelected,
  toggleJointType,
  type ConventionalUsReport,
  type UscTransducerRef
} from "../../domain/ndt/conventional-us-report-domain.js";
import { US_JOINT_MODELS, usJointSvg, type UsJointTypeId } from "../../domain/ndt/us-joint-types.js";
import { suggestNextItemNumber } from "../../domain/ndt/ndt-report-common.js";
import { formatCivilDateBR } from "../../domain/ndt/lp-consumable.js";
import { COUPLANT_OTHER, formatCatalogItemLabel, type NdtCatalogItem } from "../../domain/ndt/ndt-technical-catalog.js";
import { formatTransducerDimension, formatTransducerFrequency } from "../../domain/ndt/ndt-transducer.js";
import { calibrationStatusLabel, formatCalibrationBlockRange, resolveCalibrationStatus } from "../../domain/ndt/ndt-calibration.js";
import { NdtReportPageBase, type NdtReportPageConfig } from "./ndt-report-page-base.js";

/**
 * RELATÓRIO DE ENSAIO POR ULTRASSOM CONVENCIONAL — página de elaboração.
 * Cards 01–09 na ordem do FORM-USC. Aparelho, transdutores e bloco vêm do
 * Cadastro de Equipamentos de END; a seção 04 mostra os quatro Tipos de
 * Junta (SVG do domínio) para seleção visual, e cada linha do Registro
 * referencia Tipo de Junta → Ponto de Inspeção (só os pontos da figura).
 */
class AppConventionalUsReportPage extends NdtReportPageBase<ConventionalUsReport> {
  protected readonly config: NdtReportPageConfig = {
    method: "USC",
    documentType: "USC",
    title: "Relatório de Ensaio por Ultrassom Convencional (US)",
    subtitle: "Elaboração de relatório de ensaio de detecção de descontinuidades.",
    routeBase: "/ndt/us-conventional",
    reportLabel: "Relatório US-Convencional",
    serviceKey: "conventionalUsReportService",
    permissions: {
      view: PermissionCode.UscReportView, create: PermissionCode.UscReportCreate, edit: PermissionCode.UscReportEdit, validate: PermissionCode.UscReportValidate,
      generatePdf: PermissionCode.UscReportGeneratePdf, approve: PermissionCode.UscReportApprove, sign: PermissionCode.UscReportSign, manageSignatories: PermissionCode.UscReportManageSignatories
    },
    sections: [["01", "Identificação"], ["02", "Dados do Ensaio"], ["03", "Aparelho, Transdutores e Calibração"], ["04", "Tipos de Juntas Ensaiadas"], ["05", "Registro da Inspeção"], ["06", "Croqui"], ["07", "Observações e Parecer"], ["08", "Evidências Fotográficas"], ["09", "Signatários"]],
    itemPrefix: "USC",
    // Painel de Engenharia: o US-Convencional entra no fluxo PLH central (ENVIAR PARA O PLH → ANALISAR).
    plhReview: true
  };

  private devices: any[] = [];
  private transducerAssets: any[] = [];
  private calibrationBlocks: any[] = [];
  private couplants: NdtCatalogItem[] = [];
  private couplantOtherOpen = false;

  protected async loadSpecific(): Promise<void> {
    const r = this.report;
    const ctx = this.scopedCtx(r?.clientId);
    const assets = window.app.platform.ndtAssetService;
    const keepLinked = (list: any[], linkedIds: (string | null | undefined)[]) => list.filter(a => a.status !== "INACTIVE" || linkedIds.includes(a.id));
    const [couplants, devices, transducers, blocks] = await Promise.all([
      window.app.platform.ndtCatalogService.listSelectableForReport(ctx, "COUPLANT", "USC", r?.couplantId ?? null).catch(() => []),
      assets.listAssets(ctx, "ULTRASONIC_THICKNESS_GAUGE", true).catch(() => []),
      assets.listAssets(ctx, "ULTRASONIC_TRANSDUCER", true).catch(() => []),
      assets.listAssets(ctx, "CALIBRATION_BLOCK", true).catch(() => [])
    ]);
    this.couplants = couplants;
    this.devices = keepLinked(devices || [], [r?.deviceAssetId]);
    this.transducerAssets = keepLinked(transducers || [], (r?.transducers || []).map(t => t.assetId));
    this.calibrationBlocks = keepLinked(blocks || [], [r?.calibrationBlockAssetId]);
    this.couplantOtherOpen = !!r && !r.couplantId && !!r.couplant;
  }

  protected handleSpecificInput(target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, ds: DOMStringMap): boolean {
    if (!this.report) return false;
    if (ds["itemKey"] && ds["itemField"]) {
      const row = this.report.results.find(r => r.key === ds["itemKey"]);
      if (!row) return true;
      const field = ds["itemField"]!;
      if (field === "jointTypeId") {
        row.jointTypeId = (target.value || "") as UsJointTypeId | "";
        // O ponto pertence à figura: ao trocar o tipo, um ponto que não exista nela é descartado.
        if (row.inspectionPoint !== null && !availablePointsForRow(row.jointTypeId).includes(row.inspectionPoint)) row.inspectionPoint = null;
        this.render();
      } else if (field === "inspectionPoint") {
        const n = target.value === "" ? null : Number(target.value);
        row.inspectionPoint = n !== null && availablePointsForRow(row.jointTypeId).includes(n) ? n : null;
      } else {
        (row as any)[field] = target.value;
        if (field === "laudo") {
          const cell = this.querySelector(`[data-laudo-badge="${row.key}"]`);
          if (cell) cell.className = `laudo-badge ${this.laudoClass(row.laudo)}`;
        }
      }
      return true;
    }
    if (ds["couplantOther"] !== undefined) {
      this.report.couplantId = null;
      this.report.couplant = target.value;
      return true;
    }
    return false;
  }

  // ------------------------------------------------------- SELECTIONS

  private onCouplantChange(itemId: string) {
    if (!this.report) return;
    if (itemId === COUPLANT_OTHER) {
      this.report.couplantId = null; this.report.couplant = ""; this.couplantOtherOpen = true;
    } else {
      const item = this.couplants.find(i => i.id === itemId);
      this.report.couplantId = itemId || null;
      this.report.couplant = item ? formatCatalogItemLabel(item) : "";
      this.couplantOtherOpen = false;
    }
    this.scheduleAutosave(); this.render();
  }

  private async onDeviceChange(assetId: string) {
    if (!this.report) return;
    const device = this.devices.find(a => a.id === assetId);
    this.report.deviceAssetId = assetId || null;
    if (!device) {
      this.report.deviceManufacturer = ""; this.report.deviceModel = ""; this.report.deviceSerialNumber = ""; this.report.deviceCalibrationCert = ""; this.report.deviceCalibrationDate = ""; this.report.deviceCalibrationValidity = "";
    } else {
      this.report.deviceManufacturer = device.manufacturer || "";
      this.report.deviceModel = device.model || "";
      this.report.deviceSerialNumber = device.serialNumber || "";
      const cert = await this.latestCertificateOf(device.id);
      this.report.deviceCalibrationCert = cert?.certificateNumber || "";
      this.report.deviceCalibrationDate = cert?.issueDate || "";
      this.report.deviceCalibrationValidity = cert?.expirationDate || "";
    }
    this.scheduleAutosave(); this.render();
  }

  private async onBlockChange(assetId: string) {
    if (!this.report) return;
    const block = this.calibrationBlocks.find(a => a.id === assetId);
    this.report.calibrationBlockAssetId = assetId || null;
    if (!block) {
      this.report.calibrationBlock = ""; this.report.calibrationBlockType = ""; this.report.calibrationBlockCert = ""; this.report.calibrationBlockCertValidity = "";
    } else {
      this.report.calibrationBlock = [block.internalCode, block.serialNumber].filter(Boolean).join(" / ");
      this.report.calibrationBlockType = [block.technicalData?.blockType ? "Escalonado de degraus" : "", block.technicalData?.material || "", formatCalibrationBlockRange(block)].filter(Boolean).join(" · ");
      const cert = await this.latestCertificateOf(block.id);
      this.report.calibrationBlockCert = cert?.certificateNumber || "";
      this.report.calibrationBlockCertValidity = cert?.expirationDate || "";
    }
    this.scheduleAutosave(); this.render();
  }

  private addTransducer() {
    if (!this.report) return;
    this.report.transducers.push({ key: `USC-TR-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, assetId: null, type: "", frequency: "", angle: "", dimension: "", serialNumber: "" });
    this.scheduleAutosave(); this.render();
  }

  private removeTransducer(key: string) {
    if (!this.report) return;
    this.report.transducers = this.report.transducers.filter(t => t.key !== key);
    this.scheduleAutosave(); this.render();
  }

  private onTransducerAssetChange(key: string, assetId: string) {
    if (!this.report) return;
    const row = this.report.transducers.find(t => t.key === key);
    if (!row) return;
    const asset = this.transducerAssets.find(a => a.id === assetId);
    row.assetId = assetId || null;
    if (!asset) {
      row.type = ""; row.frequency = ""; row.angle = ""; row.dimension = ""; row.serialNumber = "";
    } else {
      const td = asset.technicalData || {};
      row.type = td.transducerType || "";
      row.frequency = formatTransducerFrequency(td.frequencyMHz);
      row.angle = td.nominalAngle !== undefined && td.nominalAngle !== null ? `${td.nominalAngle}°` : "";
      row.dimension = formatTransducerDimension(td.elementDiameter, td.dimensionUnit) || td.elementDimensions || "";
      row.serialNumber = asset.serialNumber || "";
    }
    this.scheduleAutosave(); this.render();
  }

  private onToggleJoint(id: UsJointTypeId) {
    if (!this.report) return;
    const selected = !isJointTypeSelected(this.report, id);
    this.report.jointTypes = toggleJointType(this.report.jointTypes, id, selected);
    this.scheduleAutosave(); this.render();
  }

  private addResultItem() {
    if (!this.report) return;
    const first = this.report.jointTypes[0];
    this.report.results.push({ key: `USC-ITEM-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, item: suggestNextItemNumber(this.report.results.map(r => r.item)), jointRegion: "", jointTypeId: first ? first.id : "", inspectionPoint: null, location: "", indication: "", sizing: "", laudo: "" });
    this.scheduleAutosave(); this.render();
  }

  private removeResultItem(key: string) {
    if (!this.report) return;
    this.report.results = this.report.results.filter(r => r.key !== key);
    this.scheduleAutosave(); this.render();
  }

  // -------------------------------------------------------------- CARDS

  protected renderCards(r: ConventionalUsReport, ro: boolean, canSign: boolean): string {
    return [
      this.renderCardIdentification(r, ro),
      this.renderCardTestData(r, ro),
      this.renderCardEquipment(r, ro),
      this.renderCardJointTypes(r, ro),
      this.renderCardResults(r, ro),
      this.renderCardCroqui(r, ro, "06"),
      this.renderCardObservations(r, ro, "07"),
      this.renderCardPhotos(r, ro, "08"),
      this.renderCardSignatories(r, ro, canSign, "09")
    ].join("");
  }

  private renderCardIdentification(r: ConventionalUsReport, ro: boolean): string {
    return `
      <section class="nr-card" id="nr-sec-01">
        <div class="card-head"><span class="card-title"><span class="card-num">01.</span> Identificação</span></div>
        <div class="grid cols-4">
          <label class="field"><span>Cliente</span><input class="ctl" readonly value="${this.esc(r.clientDisplayName || "—")}"></label>
          <label class="field"><span>Projeto / Contrato</span><input class="ctl" readonly value="${this.esc(r.projectContract || "—")}"></label>
          <label class="field"><span>Unidade / Área</span><input class="ctl" readonly value="${this.esc(r.unitArea || "—")}"></label>
          <label class="field"><span>Equipamento / TAG</span><input class="ctl" readonly value="${this.esc(r.equipmentTag || "—")}"></label>
        </div>
        <div class="grid cols-4" style="margin-top:12px;">
          <label class="field"><span>Tipo de Equipamento</span><input class="ctl" readonly value="${this.esc(r.equipmentTypeLabel || "—")}"></label>
          <label class="field"><span>Descrição / Componente</span><input class="ctl" data-field="component" value="${this.esc(r.component)}" placeholder="Separador de alta pressão" ${this.dis(ro)}></label>
          <label class="field"><span>Material Base</span><input class="ctl" data-field="baseMaterial" value="${this.esc(r.baseMaterial)}" placeholder="Aço carbono ASTM A516" ${this.dis(ro)}></label>
          <label class="field"><span>Temperatura do Ensaio (°C)</span><input class="ctl" data-field="testTemperature" value="${this.esc(r.testTemperature)}" placeholder="25" ${this.dis(ro)}></label>
        </div>
      </section>`;
  }

  private renderCardTestData(r: ConventionalUsReport, ro: boolean): string {
    const couplantValue = r.couplantId ?? (this.couplantOtherOpen ? COUPLANT_OTHER : "");
    return `
      <section class="nr-card" id="nr-sec-02">
        <div class="card-head"><span class="card-title"><span class="card-num">02.</span> Procedimento, Norma e Dados do Ensaio</span></div>
        ${this.renderProcedureFields(r, ro)}
        <div class="grid cols-4" style="margin-top:12px;">
          <label class="field"><span>Condição Superficial</span><input class="ctl" data-field="surfaceCondition" value="${this.esc(r.surfaceCondition)}" placeholder="Lixada / usinada / pintada…" ${this.dis(ro)}></label>
          <label class="field"><span>Técnica *</span><select class="ctl" data-field="technique" ${this.dis(ro)}>${USC_TECHNIQUE_OPTIONS.map(o => this.opt(o.value, o.label, r.technique === o.value)).join("")}</select></label>
          ${r.technique === "OUTRA" ? `<label class="field"><span>Técnica — outra</span><input class="ctl" data-field="techniqueOther" value="${this.esc(r.techniqueOther)}" ${this.dis(ro)}></label>` : ""}
          <label class="field"><span>Acoplante *</span>
            <select class="ctl" id="sel-couplant" ${this.dis(ro)}>${this.opt("", this.couplants.length === 0 ? "Nenhum acoplante cadastrado" : "Selecione...", !couplantValue)}${this.couplants.map(c => this.opt(c.id, formatCatalogItemLabel(c), couplantValue === c.id)).join("")}${this.opt(COUPLANT_OTHER, "Outro (informar)", couplantValue === COUPLANT_OTHER)}</select>
            ${this.couplantOtherOpen ? `<input class="ctl sm" data-couplant-other value="${this.esc(r.couplant)}" placeholder="Informe o acoplante" ${this.dis(ro)}>` : ""}</label>
        </div>
      </section>`;
  }

  private renderCardEquipment(r: ConventionalUsReport, ro: boolean): string {
    const devStatus = r.deviceAssetId ? resolveCalibrationStatus(r.deviceCalibrationValidity) : null;
    const blkStatus = r.calibrationBlockAssetId ? resolveCalibrationStatus(r.calibrationBlockCertValidity) : null;
    return `
      <section class="nr-card" id="nr-sec-03">
        <div class="card-head"><span class="card-title"><span class="card-num">03.</span> Aparelho, Transdutores e Calibração</span></div>
        <div class="grid cols-2">
          <div>
            <label class="field"><span>Aparelho de Ultrassom *</span>
              <select class="ctl" id="sel-device" ${this.dis(ro)}>${this.opt("", this.devices.length === 0 ? "Nenhum aparelho cadastrado para este cliente" : "Selecione...", !r.deviceAssetId)}${this.devices.map((d: any) => this.opt(d.id, `${[d.internalCode, d.manufacturer, d.model].filter(Boolean).join(" · ")}${d.serialNumber ? " — S/N " + d.serialNumber : ""}`, r.deviceAssetId === d.id)).join("")}</select></label>
            ${r.deviceAssetId ? `<div class="asset-strip"><span><b>Marca</b>${this.esc(r.deviceManufacturer || "—")}</span><span><b>Modelo</b>${this.esc(r.deviceModel || "—")}</span><span><b>Nº de Série</b>${this.esc(r.deviceSerialNumber || "—")}</span><span><b>Cert. Calibração</b>${this.esc(r.deviceCalibrationCert || "NÃO INFORMADO")}</span><span><b>Validade</b>${r.deviceCalibrationValidity ? this.esc(formatCivilDateBR(r.deviceCalibrationValidity.slice(0, 10))) : "—"}</span><span><b>Calibração</b><span class="${devStatus === "EXPIRED" ? "warn" : ""}">${this.esc(calibrationStatusLabel(r.deviceCalibrationValidity))}</span></span></div>` : ""}
          </div>
          <div>
            <label class="field"><span>Bloco de Calibração *</span>
              <select class="ctl" id="sel-block" ${this.dis(ro)}>${this.opt("", this.calibrationBlocks.length === 0 ? "Nenhum bloco cadastrado para este cliente" : "Selecione...", !r.calibrationBlockAssetId)}${this.calibrationBlocks.map((b: any) => this.opt(b.id, `${[b.internalCode, b.serialNumber].filter(Boolean).join(" / ")}${b.technicalData?.material ? " — " + b.technicalData.material : ""}`, r.calibrationBlockAssetId === b.id)).join("")}</select></label>
            ${r.calibrationBlockAssetId ? `<div class="asset-strip"><span><b>Identificação</b>${this.esc(r.calibrationBlock || "—")}</span><span><b>Tipo</b>${this.esc(r.calibrationBlockType || "—")}</span><span><b>Cert. do Bloco</b>${this.esc(r.calibrationBlockCert || "NÃO INFORMADO")}</span><span><b>Validade</b>${r.calibrationBlockCertValidity ? this.esc(formatCivilDateBR(r.calibrationBlockCertValidity.slice(0, 10))) : "—"}</span><span><b>Calibração</b><span class="${blkStatus === "EXPIRED" ? "warn" : ""}">${this.esc(calibrationStatusLabel(r.calibrationBlockCertValidity))}</span></span></div>` : ""}
          </div>
        </div>
        <div class="grid cols-2" style="margin-top:12px;">
          <label class="field"><span>Verificação / Referência</span><input class="ctl" data-field="verificationReference" value="${this.esc(r.verificationReference)}" placeholder="Ganho / referência / ajuste" ${this.dis(ro)}></label>
          <label class="field"><span>Observação</span><input class="ctl" data-field="calibrationNote" value="${this.esc(r.calibrationNote)}" ${this.dis(ro)}></label>
        </div>
        <div class="card-head" style="margin-top:14px;"><span class="card-title" style="font-size:0.76rem;">Transdutores <span class="count">${r.transducers.length}</span></span>${!ro ? `<button class="btn sm primary" id="btn-add-transducer"><i data-lucide="plus"></i> Adicionar Transdutor</button>` : ""}</div>
        <div class="table-wrap"><table class="nr-table">
          <thead><tr><th style="width:40px;">Nº</th><th>Transdutor (cadastro)</th><th>Tipo</th><th>Frequência (MHz)</th><th>Ângulo</th><th>Dimensão</th><th>Nº de Série</th><th style="width:56px;">Ações</th></tr></thead>
          <tbody>${r.transducers.length === 0 ? `<tr><td colspan="8" class="empty">Nenhum transdutor. Use "Adicionar Transdutor".</td></tr>` : r.transducers.map((t: UscTransducerRef, i) => `
            <tr>
              <td>${String(i + 1).padStart(2, "0")}</td>
              <td><select class="cell" data-transducer-key="${t.key}" ${this.dis(ro)}>${this.opt("", this.transducerAssets.length === 0 ? "Nenhum transdutor cadastrado" : "Selecione...", !t.assetId)}${this.transducerAssets.map((a: any) => this.opt(a.id, `${[a.internalCode, a.manufacturer, a.model].filter(Boolean).join(" · ")}`, t.assetId === a.id)).join("")}</select></td>
              <td>${this.esc(t.type || "—")}</td><td>${this.esc(t.frequency || "—")}</td><td>${this.esc(t.angle || "—")}</td><td>${this.esc(t.dimension || "—")}</td><td>${this.esc(t.serialNumber || "—")}</td>
              <td style="text-align:center;">${!ro ? `<button class="icon-btn danger" data-remove-transducer="${t.key}" title="Remover"><i data-lucide="trash-2"></i></button>` : ""}</td>
            </tr>`).join("")}</tbody>
        </table></div>
      </section>`;
  }

  /** Os quatro modelos, sempre visíveis; clique no card seleciona (múltipla escolha). */
  private renderCardJointTypes(r: ConventionalUsReport, ro: boolean): string {
    return `
      <section class="nr-card" id="nr-sec-04">
        <div class="card-head"><span class="card-title"><span class="card-num">04.</span> Tipos de Juntas Ensaiadas *</span><span class="hint">${r.jointTypes.length} selecionado(s) — um ensaio pode ter registros de mais de um tipo</span></div>
        <div class="joint-grid">${US_JOINT_MODELS.map(m => {
          const selected = isJointTypeSelected(r, m.id);
          return `
            <div class="joint-card ${selected ? "selected" : ""} ${ro ? "readonly" : ""}" data-joint-type="${m.id}" role="checkbox" aria-checked="${selected}" tabindex="0">
              <div class="joint-head"><span class="box">${selected ? "✓" : ""}</span><span>${this.esc(m.label)}</span></div>
              <div class="joint-figure">${usJointSvg(m.id, { stroke: "currentColor", strokeWidth: 2.2 })}</div>
              <div class="joint-points">Pontos: ${m.inspectionPoints.join(" · ")}</div>
            </div>`;
        }).join("")}</div>
      </section>`;
  }

  private renderCardResults(r: ConventionalUsReport, ro: boolean): string {
    const selectedTypes = US_JOINT_MODELS.filter(m => isJointTypeSelected(r, m.id));
    return `
      <section class="nr-card" id="nr-sec-05">
        <div class="card-head"><span class="card-title"><span class="card-num">05.</span> Registro da Inspeção / Resultados</span>${!ro ? `<button class="btn sm primary" id="btn-add-item"><i data-lucide="plus"></i> Adicionar Item</button>` : ""}</div>
        ${selectedTypes.length === 0 ? `<p class="hint" style="margin:0 0 8px 0;">Selecione ao menos um Tipo de Junta no item 04 para vincular Tipo → Ponto nas linhas.</p>` : ""}
        <div class="table-wrap"><table class="nr-table">
          <thead><tr><th style="width:50px;">Nº</th><th style="width:150px;">Junta / Região</th><th style="width:110px;">Tipo de Junta</th><th style="width:90px;">Ponto</th><th style="width:130px;">Localização</th><th>Indicação / Descontinuidade</th><th style="width:110px;">Dimensionamento</th><th style="width:170px;">Laudo</th><th style="width:50px;">Ações</th></tr></thead>
          <tbody>${r.results.length === 0 ? `<tr><td colspan="9" class="empty">Nenhum item registrado. Use "Adicionar Item".</td></tr>` : r.results.map(row => {
            const points = availablePointsForRow(row.jointTypeId);
            return `
            <tr>
              <td><input class="cell" data-item-key="${row.key}" data-item-field="item" value="${this.esc(row.item)}" ${this.dis(ro)}></td>
              <td><input class="cell" data-item-key="${row.key}" data-item-field="jointRegion" value="${this.esc(row.jointRegion)}" placeholder="Solda Circunferencial C1" ${this.dis(ro)}></td>
              <td><select class="cell" data-item-key="${row.key}" data-item-field="jointTypeId" ${this.dis(ro)}>${this.opt("", "—", !row.jointTypeId)}${selectedTypes.map(m => this.opt(m.id, m.label, row.jointTypeId === m.id)).join("")}${row.jointTypeId && !selectedTypes.some(m => m.id === row.jointTypeId) ? this.opt(row.jointTypeId, `${row.jointTypeId.replace("_", " ")} (não selecionado no 04)`, true) : ""}</select></td>
              <td><select class="cell" data-item-key="${row.key}" data-item-field="inspectionPoint" ${this.dis(ro || !row.jointTypeId)}>${this.opt("", row.jointTypeId ? "—" : "Tipo?", row.inspectionPoint === null)}${points.map(p => this.opt(String(p), String(p), row.inspectionPoint === p)).join("")}</select></td>
              <td><input class="cell" data-item-key="${row.key}" data-item-field="location" value="${this.esc(row.location)}" placeholder="Raiz / Face" ${this.dis(ro)}></td>
              <td><input class="cell" data-item-key="${row.key}" data-item-field="indication" value="${this.esc(row.indication)}" placeholder="Falta de fusão" ${this.dis(ro)}></td>
              <td><input class="cell" data-item-key="${row.key}" data-item-field="sizing" value="${this.esc(row.sizing)}" placeholder="15 mm" ${this.dis(ro)}></td>
              <td>${this.renderLaudoSelect(row.key, "item-key", "item-field", row.laudo, ro)}</td>
              <td style="text-align:center;">${!ro ? `<button class="icon-btn danger" data-remove-item="${row.key}" title="Remover"><i data-lucide="trash-2"></i></button>` : ""}</td>
            </tr>`;
          }).join("")}</tbody>
        </table></div>
      </section>`;
  }

  protected attachSpecificEvents(ro: boolean): void {
    this.querySelectorAll("[data-field='technique']").forEach(sel => sel.addEventListener("change", () => this.render()));
    if (ro) return;
    this.querySelector("#sel-couplant")?.addEventListener("change", e => this.onCouplantChange((e.target as HTMLSelectElement).value));
    this.querySelector("#sel-device")?.addEventListener("change", e => this.onDeviceChange((e.target as HTMLSelectElement).value));
    this.querySelector("#sel-block")?.addEventListener("change", e => this.onBlockChange((e.target as HTMLSelectElement).value));
    this.querySelector("#btn-add-transducer")?.addEventListener("click", () => this.addTransducer());
    this.querySelectorAll("[data-transducer-key]").forEach(sel => sel.addEventListener("change", e => this.onTransducerAssetChange((sel as HTMLElement).dataset["transducerKey"]!, (e.target as HTMLSelectElement).value)));
    this.querySelectorAll("[data-remove-transducer]").forEach(btn => btn.addEventListener("click", () => this.removeTransducer((btn as HTMLElement).dataset["removeTransducer"]!)));
    this.querySelectorAll(".joint-card").forEach(card => {
      const id = (card as HTMLElement).dataset["jointType"] as UsJointTypeId;
      card.addEventListener("click", () => this.onToggleJoint(id));
      card.addEventListener("keydown", e => { if ((e as KeyboardEvent).key === " " || (e as KeyboardEvent).key === "Enter") { e.preventDefault(); this.onToggleJoint(id); } });
    });
    this.querySelector("#btn-add-item")?.addEventListener("click", () => this.addResultItem());
    this.querySelectorAll("[data-remove-item]").forEach(btn => btn.addEventListener("click", () => this.removeResultItem((btn as HTMLElement).dataset["removeItem"]!)));
  }
}

if (!customElements.get("app-conventional-us-report-page")) {
  customElements.define("app-conventional-us-report-page", AppConventionalUsReportPage);
}
